import { Loader2 } from 'lucide-react'
import qs from 'query-string'
import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { resendRegistrationVerification, verifyRegistration } from '../../api/registrationApi'
import { applySignUpSession } from './signUpSession'
import { getFriendlyErrorMessage } from '../../utils/errorMessage'
import { validations } from '../../utils/utils'
import { ErrorBox, InfoBox, RegistrationShell, fieldClass, linkButtonClass, primaryButtonClass } from './RegistrationShell'

/**
 * /register/verify?token=… — consumes the email link. Confirming the email creates the account
 * (no package yet) and signs the owner in once, straight on to /packages to choose and buy one;
 * everything stays locked until they do. A later click on the same link just says "sign in". A sign-up that was already at Checkout before this
 * change goes back to its plan page. An expired link offers to send a fresh one (the response
 * never says whether the address is known).
 */
export const VerifyEmail = () => {
  const history = useHistory()
  const token = qs.parse(window.location.search)?.token
  const [state, setState] = useState({ phase: 'verifying', status: null, message: null })
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [resent, setResent] = useState(false)
  const started = useRef(false)

  useEffect(() => {
    // The token is stable server-side, but one verify call is enough: StrictMode mounts twice in dev.
    if (started.current) return
    started.current = true
    if (!token) {
      setState({ phase: 'error', status: 404, message: 'This link is not valid.' })
      return
    }
    verifyRegistration(token)
      .then((view) => {
        if (view?.status === 'PROVISIONED' && view?.auth) {
          // Just created: signed in once — go choose a package.
          return applySignUpSession(view.auth, view.owner_email)
            .then(() => history.replace('/packages'))
            .catch(() => setState({ phase: 'provisioned', email: view?.owner_email }))
        }
        if (view?.status === 'PROVISIONED') {
          setState({ phase: 'provisioned', email: view?.owner_email })
          return
        }
        history.replace(`/register/plan?token=${encodeURIComponent(view?.token || token)}`)
      })
      .catch((error) => {
        setState({ phase: 'error', status: error?.response?.status, message: getFriendlyErrorMessage(error) })
      })
  }, [token, history])

  const onResend = async (event) => {
    event.preventDefault()
    if (!validations.email(email)) {
      setEmailError(true)
      return
    }
    setEmailError(false)
    try {
      await resendRegistrationVerification(email.trim().toLowerCase())
    } catch (error) {
      // Treated as sent either way; see registrationApi.
    }
    setResent(true)
  }

  if (state.phase === 'verifying') {
    return (
      <RegistrationShell width='narrow' title='Confirming your email…'>
        <div className='flex justify-center py-6' aria-busy='true'>
          <Loader2 className='h-6 w-6 animate-spin text-white/70' />
        </div>
      </RegistrationShell>
    )
  }

  if (state.phase === 'provisioned') {
    return (
      <RegistrationShell width='narrow' title='Your email is confirmed'>
        <InfoBox>
          Your account is ready. Sign in{state.email ? ` with ${state.email}` : ''} and the password you chose, then choose a package in
          Settings → Billing to start using ADAS Intelligence.
        </InfoBox>
        <button type='button' onClick={() => history.push('/authenticate')} className={`${primaryButtonClass} mt-5`}>
          Sign in
        </button>
      </RegistrationShell>
    )
  }

  const expired = state.status === 410
  return (
    <RegistrationShell width='narrow' title={expired ? 'This link has expired' : 'This link isn’t valid'}>
      <ErrorBox>{state.message}</ErrorBox>
      {resent ? (
        <InfoBox>If that address has a pending sign-up, a new link is on its way.</InfoBox>
      ) : (
        <form onSubmit={onResend} noValidate className='mt-4 space-y-3'>
          <input
            type='email'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder='Email you signed up with'
            className={`${fieldClass} ${emailError ? 'border-red-400/60' : ''}`}
            aria-label='Email you signed up with'
          />
          {emailError && <div className='pl-1 text-[11px] text-red-300'>Enter a valid email address</div>}
          <button type='submit' className={primaryButtonClass}>
            Send a new link
          </button>
        </form>
      )}
      <div className='mt-4 flex flex-col items-center gap-2'>
        <button type='button' onClick={() => history.push('/register')} className={linkButtonClass}>
          Start a new sign-up
        </button>
        <button type='button' onClick={() => history.push('/authenticate')} className={linkButtonClass}>
          Back to sign in
        </button>
      </div>
    </RegistrationShell>
  )
}
